/**
 * UsageLogView — Tier-3b of the Cloud GM AI Control Hub.
 *
 * Shows quota card + 4-tile summary + per-feature breakdown + recent-calls
 * table. Backed by /api/admin/ai-control/usage endpoints.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw, Loader2, History, Trash2, Sparkles, CheckCircle2, XCircle,
} from 'lucide-react';
import QuotaCard from '../QuotaCard';
import { getAuthHeaders } from '../../utils/authHeaders';
import { ProviderLogo } from './ProviderLogo';
import { fmtTs } from './constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function UsageLogView({ language, provider }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API_URL}/api/admin/ai-control/usage`, {
          params: { provider: provider.provider, limit: 200 },
          headers: getAuthHeaders(),
        }),
        axios.get(`${API_URL}/api/admin/ai-control/usage/summary`, {
          params: { provider: provider.provider },
          headers: getAuthHeaders(),
        }),
      ]);
      setRows(r1.data?.rows || []);
      setSummary(r2.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  }, [provider.provider]);

  useEffect(() => { load(); }, [load]);

  const clearAll = async () => {
    const msg = language === 'ar'
      ? 'حذف كل سجل الاستخدام؟ هذا غير قابل للتراجع.'
      : 'Clear ALL usage history? This cannot be undone.';
    if (!window.confirm(msg)) return;
    setClearing(true);
    try {
      await axios.delete(`${API_URL}/api/admin/ai-control/usage`, { headers: getAuthHeaders() });
      toast.success(language === 'ar' ? 'تم مسح السجل' : 'Log cleared');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="ai-usage-log">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ProviderLogo provider={provider.provider} size={44} />
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <History className="h-5 w-5 text-blue-300" />
              {language === 'ar' ? 'سجل الاستخدام' : 'Usage Log'}
            </h3>
            <p className="text-xs text-[#A1A1AA] mt-1">
              {language === 'ar' ? provider.label_ar : provider.label_en}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="p-2 rounded-lg bg-white/5 border border-white/10 text-[#A1A1AA] hover:text-white"
            data-testid="ai-usage-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={clearAll}
            disabled={clearing}
            className="px-3 py-2 text-xs rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 disabled:opacity-50 flex items-center gap-2"
            data-testid="ai-usage-clear"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {language === 'ar' ? 'مسح' : 'Clear'}
          </button>
        </div>
      </div>

      {summary && <QuotaCard provider={provider.provider} language={language} />}

      {summary && <UsageSummaryTiles summary={summary} language={language} />}

      {summary?.by_feature?.length > 0 && (
        <UsageByFeatureTable rows={summary.by_feature} language={language} />
      )}

      <UsageCallsTable rows={rows} loading={loading} language={language} />
    </div>
  );
}

// ── sub-components ──────────────────────────────────────────────────────────

function UsageSummaryTiles({ summary, language }) {
  const tiles = [
    { label: language === 'ar' ? 'إجمالي النداءات' : 'Total Calls', value: summary.total_calls, color: 'text-white' },
    { label: language === 'ar' ? 'إجمالي التوكنز' : 'Total Tokens', value: (summary.total_tokens || 0).toLocaleString(), color: 'text-blue-300' },
    { label: language === 'ar' ? 'التكلفة الكلية' : 'Total Cost', value: `$${(summary.total_cost_usd || 0).toFixed(4)}`, color: 'text-[#d4af37]' },
    { label: language === 'ar' ? 'الميزات النشطة' : 'Active Features', value: summary.by_feature?.length || 0, color: 'text-emerald-300' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ai-usage-summary">
      {tiles.map((t) => (
        <div key={t.label} className="bg-white/[0.02] rounded-xl border border-white/10 p-3">
          <div className="text-[10px] text-[#A1A1AA]">{t.label}</div>
          <div className={`text-xl font-bold ${t.color} mt-1`}>{t.value}</div>
        </div>
      ))}
    </div>
  );
}

function UsageByFeatureTable({ rows, language }) {
  return (
    <div className="bg-white/[0.02] rounded-xl border border-white/10 overflow-hidden" data-testid="ai-usage-by-feature">
      <div className="px-4 py-2 border-b border-white/5 text-xs font-bold text-[#A1A1AA]">
        {language === 'ar' ? 'تفصيل حسب الميزة' : 'Per-Feature Breakdown'}
      </div>
      <div className="divide-y divide-white/5">
        {rows.map((row, idx) => (
          <div key={`${row.feature_id}-${idx}`} className="px-4 py-2.5 flex items-center gap-3 text-xs hover:bg-white/[0.02]">
            <div className="flex-1 min-w-0">
              <div className="font-bold text-white truncate">{row.feature_id || '—'}</div>
              <div className="text-[10px] text-[#71717A] font-mono mt-0.5">{row.model}</div>
            </div>
            <UsageMetric label={language === 'ar' ? 'نداءات' : 'Calls'} value={row.calls} color="text-white" />
            <UsageMetric
              label={language === 'ar' ? 'توكنز' : 'Tokens'}
              value={(row.total_tokens || 0).toLocaleString()}
              color="text-blue-300"
            />
            <UsageMetric
              label={language === 'ar' ? 'تكلفة' : 'Cost'}
              value={`$${(row.cost_usd || 0).toFixed(4)}`}
              color="text-[#d4af37]"
            />
            {row.errors > 0 && (
              <UsageMetric label={language === 'ar' ? 'أخطاء' : 'Errors'} value={row.errors} color="text-red-300" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function UsageMetric({ label, value, color }) {
  return (
    <div className="text-center">
      <div className="text-[10px] text-[#71717A]">{label}</div>
      <div className={`font-bold ${color}`}>{value}</div>
    </div>
  );
}

function UsageCallsTable({ rows, loading, language }) {
  return (
    <div className="bg-white/[0.02] rounded-xl border border-white/10 overflow-hidden" data-testid="ai-usage-table">
      <div className="px-4 py-2 border-b border-white/5 text-xs font-bold text-[#A1A1AA] flex items-center justify-between">
        <span>{language === 'ar' ? 'آخر النداءات' : 'Recent Calls'}</span>
        <span>{rows.length} {language === 'ar' ? 'صف' : 'rows'}</span>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-7 w-7 animate-spin text-[#d4af37]" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-[#71717A]">
          <Sparkles className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>{language === 'ar' ? 'لا توجد سجلات بعد' : 'No calls logged yet'}</p>
          <p className="text-[10px] mt-1">
            {language === 'ar'
              ? 'ستظهر هنا مع كل نداء للذكاء الاصطناعي'
              : 'Rows appear as AI calls happen'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/[0.02] text-[10px] text-[#71717A] uppercase">
              <tr>
                <th className="px-3 py-2 text-start font-bold">{language === 'ar' ? 'الوقت' : 'Time'}</th>
                <th className="px-3 py-2 text-start font-bold">{language === 'ar' ? 'الميزة' : 'Feature'}</th>
                <th className="px-3 py-2 text-start font-bold">{language === 'ar' ? 'الموديل' : 'Model'}</th>
                <th className="px-3 py-2 text-end font-bold">In</th>
                <th className="px-3 py-2 text-end font-bold">Out</th>
                <th className="px-3 py-2 text-end font-bold">{language === 'ar' ? 'تكلفة' : 'Cost'}</th>
                <th className="px-3 py-2 text-center font-bold">{language === 'ar' ? 'الحالة' : 'Status'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-white/[0.02]" data-testid={`ai-usage-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-[10px] text-[#A1A1AA]">{fmtTs(r.ts)}</td>
                  <td className="px-3 py-2 text-white truncate max-w-[180px]">{r.feature_id || '—'}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-blue-300 truncate max-w-[140px]">{r.model}</td>
                  <td className="px-3 py-2 text-end font-mono text-white/80">{(r.input_tokens || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-end font-mono text-white/80">{(r.output_tokens || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-end font-mono text-[#d4af37]">${(r.cost_usd || 0).toFixed(5)}</td>
                  <td className="px-3 py-2 text-center">
                    {r.ok ? (
                      <span className="inline-flex items-center gap-1 text-emerald-300 text-[10px]">
                        <CheckCircle2 className="h-3 w-3" />
                        OK
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-300 text-[10px]" title={r.error || ''}>
                        <XCircle className="h-3 w-3" />
                        ERR
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
